<template>
  <div v-if="groupChatPost" class="edit-post-wrapper">
    <proa-textarea
      v-model="groupChatPost.content"
      ref="textarea"
      :attachments="groupChatPost.groupChatAttachmentsAttributes"
      :defaultValue="groupChatPost.content"
      :placeholder="$t('common.chatTextAreaPlaceHolder')"
      :quotationMode="quotationMode"
      :disabled="httpRequesting"
      :key="groupChatPostRenderingKey"
      @toggleQuotationMode="(mode) => { this.quotationMode = mode }"
      @onClickRemoveAttachment="(index) => { this.$emit('onClickRemoveAttachment', index) }"
      @onSubmitChatPost="onSubmitChatPost"
    />
    <div class="chat-assets">
      <div class="post-tools">
        <!-- mention select modal -->
        <div
          v-if="mentionUserSelectModal"
          class="mention-select-modal"
          v-click-outside="() => { mentionUserSelectModal = false }"
        >
          <div class="search-form-wrapper">
            <proa-search-input
              v-model="searchMentionUserName"
              type="text"
              :placeholder="$t('common.searchForMembers')"
              dark
            />
          </div>
          <div class="mention-users-flex-item">
            <div
              :class="['mention-select-wrapper', { 'pb-50': mentionedUsers.length !== 0 }]"
            >
              <div
                v-if="enabledMentionAll"
                class="user"
                @click="onClickMentionAll"
              >
                <img class="mention-all" :src="require('@/assets/images/chat_tool_icons/mention_all.png')">
                <span class="display-name">{{ $t('common.all') }}</span>
              </div>
              <div
                v-for="user in users"
                :key="user.id"
                class="user"
              >
                <proa-check-box
                  :model="mentionedUsers"
                  :value="`@[${user.meta.displayName}:${user.id}] `"
                  @change="newModel => { mentionedUsers = newModel }"
                >
                  <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="xs"/>
                  <span class="display-name">{{ user.meta.displayName }}</span>
                </proa-check-box>
              </div>
            </div>
          </div>
          <div class="mention-button-flex-item">
            <div :class="['add-mention-button', { 'slideUp': mentionedUsers.length !== 0 }]">
              <proa-button
                class="default-button-width"
                @click.native="onClickAddMentionUser"
              >
                {{ $t('common.addMention') }}
              </proa-button>
            </div>
          </div>
        </div>
        <!-- task select modal -->
        <template v-if="groupChat.isProject()">
          <div
            v-if="taskTagSelectModal"
            class="task-select-modal"
            v-click-outside="() => { taskTagSelectModal = false }"
          >
            <div class="search-form-wrapper">
              <proa-search-input
                v-model="searchTaskTitle"
                type="text"
                :placeholder="$t('common.searchForTasks')"
                dark
              />
            </div>
            <div
              v-for="resource in taskCardsGroupByTaskList"
              :key="resource.taskListTitle"
              class="task-card"
            >
              <template v-if="resource.taskCards.length">
                <div class="task-card-group-header" @click.stop="() => {}">{{ resource.taskListTitle }}</div>
                <div
                  v-for="taskCard in resource.taskCards"
                  :key="taskCard.id"
                  class="task-card-group-item"
                  @click="onClickTaskTag(taskCard)"
                >
                  <span>{{ taskCard.title }}</span>
                </div>
              </template>
            </div>
          </div>
        </template>
        <!-- tool icons -->
        <proa-tool-icon
          :src="require('@/assets/images/chat_tool_icons/mention.png')"
          :noSmMargin="type == 'edit'"
          @click.native.stop="onClickMentionUserSelect"
        />
        <proa-tool-icon
          v-if="groupChat.isProject()"
          :src="require('@/assets/images/chat_tool_icons/task_tag.png')"
          :noSmMargin="type == 'edit'"
          @click.native.stop="onClickTaskTagSelect"
        />
        <proa-tool-icon
          :src="require('@/assets/images/chat_tool_icons/attachment.png')"
          :noSmMargin="type == 'edit'"
          @click.native="onClickNewAttachment"
        />
        <proa-tool-icon
          :src="require('@/assets/images/chat_tool_icons/quotation.png')"
          :noSmMargin="type == 'edit'"
          :active="quotationMode"
          @click.native="onClickQuotationMode"
        />
      </div>
      <div v-if="type == 'new'">
        <span v-if="enabledSendChatPost" class="chat-send-support">{{ $t('common.chatSendSupport') }}</span>
        <proa-button
          variant="primary"
          class="chat-send"
          :disabled="httpRequesting || !enabledSendChatPost"
          @click.native="onSubmitChatPost"
        >
          <img :src="require('@/assets/images/chat_tool_icons/send.png')"/>
        </proa-button>
      </div>
      <div v-else-if="type == 'edit'">
        <span v-if="enabledSendChatPost" class="chat-send-support">{{ $t('common.chatSendSupport') }}</span>
        <proa-button
          variant="light"
          class="edit-cancel"
          :disabled="httpRequesting"
          @click.native="emit('onCancelEditChatPost')"
        >
          <span class="d-none d-sm-block">{{ $t('common.cancel') }}</span>
          <img class="d-block d-sm-none" :src="require('@/assets/images/chat_tool_icons/thread.png')"/>
        </proa-button>
        <proa-button
          variant="primary"
          class="chat-save"
          :disabled="httpRequesting || !enabledSendChatPost"
          @click.native="onSubmitChatPost"
        >
          <span class="d-none d-sm-block">{{ $t('common.save') }}</span>
          <img class="d-block d-sm-none" :src="require('@/assets/images/chat_tool_icons/send.png')"/>
        </proa-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ClickOutside from 'vue-click-outside'
import User from '@/store/models/User'
import TaskList from '@/store/models/TaskList'
import TaskCard from '@/store/models/TaskCard'

export default {
  name: 'GroupChatPostEditor',
  props: {
    groupChat: Object,
    groupChatPost: Object,
    groupChatPostRenderingKey: Number,
    type: {
      type: String,
      default: 'new'
    }
  },
  directives: {
    ClickOutside
  },
  computed: {
    enabledMentionAll: function() {
      return 'all'.indexOf(this.searchMentionUserName) != -1
    },
    enabledSendChatPost: function() {
      return this.groupChatPost.content.trim() ||
             this.groupChatPost.groupChatAttachmentsAttributes.filter(attachment => !attachment._destroy).length >= 1
    },
    users: function() {
      if (this.groupChat.groupChatType === 'publicChat') {
        return User.query().where((user) => {
          return user.creatable && user.meta.displayName.indexOf(this.searchMentionUserName) != -1
        }).get()
      } else {
        return User.query().whereIdIn(this.groupChat.userIds.map(Number)).where((user) => {
          return user.meta.displayName.indexOf(this.searchMentionUserName) != -1
        }).get()
      }
    },
    taskCardsGroupByTaskList: function() {
      const taskLists = TaskList.query().where((taskList) => {
        return taskList.projectId == this.groupChat.projectId && taskList.listType != 'archive'
      }).orderBy('listIndex', 'asc').get()
      return taskLists.map(taskList => {
        return {
          taskListTitle: taskList.title,
          taskCards: TaskCard.query().where((taskCard) => {
            return taskCard.taskListId == taskList.id && taskCard.title.indexOf(this.searchTaskTitle) != -1
          }).get()
        }
      })
    },
    ...mapGetters({
      httpRequesting: 'httpRequesting'
    })
  },
  data() {
    return {
      quotationMode: false,
      mentionUserSelectModal: false,
      taskTagSelectModal: false,
      searchMentionUserName: '',
      searchTaskTitle: '',
      mentionedUsers: []
    }
  },
  methods: {
    emit: function(emitString) {
      this.$emit(emitString)
      this.resetMode()
    },
    inputCustomTag: function(tagString) {
      const textAreaComponent = this.$refs.textarea
      const position = textAreaComponent.$refs.proaTextarea.selectionStart
      const currentValue = textAreaComponent.$refs.proaTextarea.value
      textAreaComponent.$refs.proaTextarea.value = currentValue.slice(0, position)
                                                  + tagString
                                                  + currentValue.slice(position, currentValue.length)
      textAreaComponent.fireInputTextarea()
    },
    resetMode: function() {
      this.quotationMode = false
    },
    onClickMentionUserSelect: function() {
      if (this.httpRequesting) return
      this.taskTagSelectModal = false
      this.searchMentionUserName = ''
      this.mentionUserSelectModal = true
    },
    onClickAddMentionUser: function () {
      for (const mentionedUser of this.mentionedUsers) {
        this.inputCustomTag(mentionedUser)
      }
      this.mentionUserSelectModal = false
    },
    onClickTaskTagSelect: function() {
      if (this.httpRequesting) return
      this.mentionUserSelectModal = false
      this.searchTaskTitle = ''
      this.taskTagSelectModal = true
    },
    onClickMentionAll: function() {
      this.inputCustomTag('@[all:] ')
      this.mentionUserSelectModal = false
    },
    onClickTaskTag: function(taskCard) {
      this.inputCustomTag(`#[${taskCard.title}:${taskCard.id}] `)
      this.taskTagSelectModal = false
    },
    onClickNewAttachment: function() {
      if (this.httpRequesting) return
      this.$emit('onClickNewAttachment')
    },
    onClickQuotationMode: function() {
      if (this.httpRequesting) return
      this.quotationMode = !this.quotationMode
    },
    onSubmitChatPost: function() {
      if (!this.enabledSendChatPost) return
      this.emit('onSubmitChatPost')
    }
  },
  watch: {
    mentionUserSelectModal: function(value) {
      if (!value) {
        this.mentionedUsers = []
      }
    }
  }
}
</script>

<style scoped lang="scss">
</style>
